import React, { useRef, useState, useEffect, Dispatch, SetStateAction } from 'react';
import cls from 'classnames';
import { useClickAway } from 'ahooks';
import { Dropdown, Input, Icon, Bubble } from 'tea-component';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { IMetricsOptionItem } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import { currentOptionsDisabledTip, isMetricSearchOptionDisabled, normalizeMetricSearchDateGranularity } from './utils';

import './index.scss';

interface IMetricSearchBoxProps {
  inputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
  onChange: Dispatch<SetStateAction<string[]>>;
  setSourcesValue: Dispatch<SetStateAction<string[]>>;
  selectMetricsList: IMetricsOptionItem[];
  searchOptions?: IMetricsOptionItem[];
  dateGranularityType: string;
}

function MetricSearchBox({
  inputValue,
  setInputValue,
  onChange,
  setSourcesValue,
  selectMetricsList,
  searchOptions,
  dateGranularityType,
}: IMetricSearchBoxProps) {
  const { formatMessage, isEN } = useIntlExtend();

  const searchBoxRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);

  // 下拉选项浮框完全受控
  const [searchOptionsVisible, setSearchOptionsVisible] = useState(false);

  const normalizedDateGranularityType = normalizeMetricSearchDateGranularity(
    dateGranularityType,
  );
  const isMonthly = normalizedDateGranularityType === 'monthly';
  const middle = isMonthly ? 'monthly_' : '';
  const splitor = 'dbi_metric_';

  // TODO:一个根据特定时间粒度进行指标显示调整的修改点，在 montly 粒度下添加显示前缀，单独进行处理，临时方案
  const needProcessMetricsValue = [
    'ampere_d1',
    'ampere_d7',
    'ampere_d14',
    'ampere_d28',
    'ampere_d60',
    'ampere_rolling_d1',
    'ampere_rolling_d7',
    'ampere_rolling_d14',
    'ampere_rolling_d28',
    'ampere_rolling_d60',
  ];

  // 点击搜索框及浮窗以外元素隐藏浮窗，其余正常展示
  useClickAway(() => {
    setSearchOptionsVisible(false);
  }, [containerRef, overlayContainerRef]);

  // 点击输入框内容区将搜索输入框聚焦
  const handleContainerClick = () => {
    searchBoxRef.current?.focus();
    setSearchOptionsVisible(true);
  };

  // 浮窗隐藏后，点击搜索输入框
  const handleSearchBoxClick = () => {
    setSearchOptionsVisible(true);
  };

  // 点击清除按钮
  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setInputValue('');
    onChange([]);
  };

  // 删除当前选中 tag
  const handleTagItemCloseClick = (
    metric: IMetricsOptionItem,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const setStateCallback = (prev: string[]) => {
      const curr = prev?.slice();
      return curr.filter((item: string) => item !== metric.value);
    };

    onChange(setStateCallback);
  };

  // 点击当前选项
  const handleOptionsItemClick = (option: IMetricsOptionItem) => {
    const setStateCallback = (prev: string[]) => {
      const curr = prev?.slice();
      /**
       * 移除操作
       */
      if (curr.includes(option.value)) {
        return curr.filter((item: string) => item !== option.value);
      }
      /**
       * 选中操作
       */
      // 选中时判断当前指标是否在选中的 Sources 内，如不在则更改 Sources 的值
      setSourcesValue((prevSource) => {
        if (!prevSource?.includes('all') && !prevSource?.includes(option.dataSource)) {
          return [...prevSource, option.dataSource];
        }
        return prevSource;
      });

      return [option.value, ...(curr || [])];
    };

    onChange(setStateCallback);
  };

  // Input 有输入值的时候展示筛选选项浮窗
  useEffect(() => {
    setSearchOptionsVisible(Boolean(inputValue));
  }, [inputValue]);

  return (
    <div
      ref={containerRef}
      onClick={handleContainerClick}
      className="dbi-metric-search-info-container"
    >
      {/* 选中项 tags 展示 */}
      {Boolean(selectMetricsList?.length) && (
        <div className="dbi-metric-search-selected-metrics">
          {selectMetricsList?.map((item) => {
            const iconSrc = getDataSourceIcon(
              item?.icon ? item?.icon : item?.dataSource || item?.value,
            );
            const textStrArr = item?.text?.split(splitor);
            const rawMetricText = textStrArr?.[textStrArr?.length - 1];
            return (
              <div className="dbi-selected-metrics-item" key={item.value}>
                {iconSrc && <img className="dbi-selected-metrics-item-icon" src={iconSrc} alt="" />}
                {/* {formatMessage({ id: item.text })} */}
                {isMonthly && needProcessMetricsValue.includes(item?.value)
                  ? formatMessage({ id: `${splitor}${middle}${rawMetricText}` })
                  : formatMessage({ id: item?.text })}
                <Icon
                  type="close"
                  onClick={(event) => handleTagItemCloseClick(item, event)}
                />
              </div>
            );
          })}
        </div>
      )}

      <Dropdown
        matchButtonWidth
        appearance="pure"
        clickClose={false}
        style={{ display: 'block' }}
        updateDeps={[selectMetricsList]}
        open={Boolean(inputValue) && searchOptionsVisible}
        placementOffset={10}
        closeDelay={50}
        button={
          <div
            className={cls('dbi-metric-search-box-container', {
              'no-selected': !selectMetricsList?.length,
            })}
          >
            <Input
              ref={searchBoxRef}
              className="dbi-metric-search-box-input"
              // placeholder={formatMessage({ id: 'dbi_info_search_metric' }) as string}
              placeholder={formatMessage({ id: 'dbi_info_metric_search_placeholder' }) as string}
              value={inputValue}
              onClick={handleSearchBoxClick}
              onChange={(value) => {
                setInputValue(value);
              }}
            />
            {/* 清空按钮展示条件：有选中值 || 搜索框有输入搜索文案 */}
            {(Boolean(selectMetricsList?.length) || inputValue) && (
              <Icon
                type="dismiss"
                onClick={(event) => handleClear(event)}
                className="dbi-metric-search-box-clear-icon"
              />
            )}
            <Icon type="search" className="dbi-metric-search-box-search-icon" />
          </div>
        }
      >
        <div ref={overlayContainerRef}>
          {/* 选项多选列表 */}
          {Boolean(searchOptions?.length) && (
            <div className="dbi-search-box-dropdown-content">
              {searchOptions?.map((item, index: number) => {
                const disabled = isMetricSearchOptionDisabled(
                  item,
                  dateGranularityType,
                );
                return (
                  <Bubble
                    key={index}
                    content={
                      disabled
                        ? item?.messagekey
                          ? (formatMessage({ id: item.messagekey }) as string)
                          : currentOptionsDisabledTip(item?.dateEnum, isEN)
                        : null
                    }
                  >
                    <div
                      onClickCapture={() => !disabled && handleOptionsItemClick(item)}
                      className={cls({
                        'dbi-search-box-dropdown-option-item': true,
                        disabled,
                      })}
                    >
                      {item.text}
                    </div>
                  </Bubble>
                );
              })}
            </div>
          )}

          {/* 无查询数据选项 */}
          {!searchOptions?.length && (
            <div className="dbi-search-box-dropdown-content-no-result">
              {formatMessage({ id: 'dbi_no_results_found' })}
            </div>
          )}
        </div>
      </Dropdown>
    </div>
  );
}

export default MetricSearchBox;
